/**
 * StateOpsPage (ENR-037, ENR-038).
 *
 * The State-Ops surface: a "Submissions / Tracker" view of the auto-generated
 * weekly state-submission tracker, and a "Responses" inbox where inbound state
 * responses are classified into structured outcomes. Two tabs over one page so
 * State Ops has a single nav home. Both lists degrade gracefully when the
 * sibling backend hasn't wired them (friendly empty state, never a crash).
 */
import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useStateOps, type StateResponse, type StateTracker } from '../hooks/useStateOps';
import { colors, radius } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { RoleImpactStrip } from '../components/PlatformImpactStrip';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { EmptyState } from '../components/EmptyState';
import { useShellSlot } from '../components/shellContext';

type Tab = 'tracker' | 'responses';

const CLASSIFICATIONS = [
  { value: 'approval', label: 'Approval' },
  { value: 'denial', label: 'Denial' },
  { value: 'info_request', label: 'Info request' },
  { value: 'other', label: 'Other' },
];

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

export function StateOpsPage() {
  const {
    trackers,
    responses,
    trackersAvailable,
    responsesAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    classifyResponse,
  } = useStateOps();
  const [tab, setTab] = useState<Tab>('tracker');

  useShellSlot({ breadcrumb: 'State Ops' });

  const unclassified = responses.filter((r) => r.classification === 'unclassified').length;

  const trackerColumns: Column<StateTracker>[] = [
    {
      key: 'case',
      header: 'Case',
      render: (t) => (
        <Link to={`/cases/${encodeURIComponent(t.case_id)}`} style={caseLink}>
          {t.case_id}
        </Link>
      ),
    },
    { key: 'program', header: 'State program', render: (t) => t.state_program },
    { key: 'status', header: 'Status', render: (t) => <StatusBadge value={t.status} /> },
    { key: 'submitted', header: 'Submitted', render: (t) => <span style={muted}>{fmtDateTime(t.submitted_at)}</span> },
    { key: 'due', header: 'Decision due', render: (t) => <span style={muted}>{fmtDate(t.due_at)}</span> },
    { key: 'receipt', header: 'Receipt', render: (t) => <span style={muted}>{t.receipt_id ?? '—'}</span> },
  ];

  const responseColumns: Column<StateResponse>[] = [
    {
      key: 'case',
      header: 'Case',
      render: (r) => (
        <Link to={`/cases/${encodeURIComponent(r.case_id)}`} style={caseLink}>
          {r.case_id}
        </Link>
      ),
    },
    { key: 'program', header: 'Program', render: (r) => <span style={muted}>{r.state_program ?? '—'}</span> },
    { key: 'summary', header: 'Summary', render: (r) => r.summary ?? <span style={muted}>—</span> },
    {
      key: 'class',
      header: 'Classification',
      render: (r) =>
        r.classification === 'unclassified' ? (
          <StatusBadge status="warning" label="Unclassified" />
        ) : (
          <StatusBadge value={r.classification} />
        ),
    },
    { key: 'received', header: 'Received', render: (r) => <span style={muted}>{fmtDateTime(r.received_at)}</span> },
    {
      key: 'action',
      header: 'Classify',
      render: (r) => <ClassifyControl response={r} isWorking={isWorking} onClassify={classifyResponse} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="State Ops"
        subtitle="Weekly state-submission tracker (ENR-037) and the structured state-response inbox (ENR-038)."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      <RoleImpactStrip />

      <div style={tabBar} role="tablist" aria-label="State Ops views">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tracker'}
          onClick={() => setTab('tracker')}
          style={{ ...tabBtn, ...(tab === 'tracker' ? tabActive : {}) }}
        >
          Submissions / Tracker
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'responses'}
          onClick={() => setTab('responses')}
          style={{ ...tabBtn, ...(tab === 'responses' ? tabActive : {}) }}
        >
          Responses
          {unclassified > 0 && <span style={countPill}>{unclassified}</span>}
        </button>
      </div>

      {error && (
        <p style={errorText} role="alert">
          {error.message}
        </p>
      )}
      {actionError && (
        <p style={errorText} role="alert">
          {actionError.message}
        </p>
      )}

      {tab === 'tracker' && (
        <Card title="State submission tracker" padded={false}>
          {isLoading ? (
            <div style={{ padding: 18 }}>
              <LoadingIndicator text="Loading state trackers…" />
            </div>
          ) : !trackersAvailable ? (
            <EmptyState
              title="Tracker not available yet"
              description="The weekly state-submission tracker isn't connected in this environment. Submitted packets will appear here."
            />
          ) : (
            <DataTable
              columns={trackerColumns}
              rows={trackers}
              rowKey={(t) => t.id}
              emptyTitle="No submissions tracked"
              emptyDescription="Packets submitted to a state agency are auto-tracked here weekly."
            />
          )}
        </Card>
      )}

      {tab === 'responses' && (
        <Card title="State responses" padded={false}>
          {isLoading ? (
            <div style={{ padding: 18 }}>
              <LoadingIndicator text="Loading state responses…" />
            </div>
          ) : !responsesAvailable ? (
            <EmptyState
              title="Response inbox not available yet"
              description="The structured state-response intake isn't connected in this environment. Inbound responses will appear here for classification."
            />
          ) : (
            <DataTable
              columns={responseColumns}
              rows={responses}
              rowKey={(r) => r.id}
              emptyTitle="No state responses"
              emptyDescription="Inbound responses from state agencies appear here for structured intake."
            />
          )}
        </Card>
      )}
    </>
  );
}

function ClassifyControl({
  response,
  isWorking,
  onClassify,
}: {
  response: StateResponse;
  isWorking: boolean;
  onClassify: (id: string, body: { classification: string; note?: string }) => Promise<boolean>;
}) {
  const [value, setValue] = useState(
    response.classification !== 'unclassified' ? response.classification : CLASSIFICATIONS[0].value
  );

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select value={value} onChange={(e) => setValue(e.target.value)} style={selectStyle} aria-label="Classification">
        {CLASSIFICATIONS.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={isWorking}
        onClick={() => onClassify(response.id, { classification: value })}
      >
        Classify
      </button>
    </div>
  );
}

const tabBar: CSSProperties = { display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${colors.border}` };
const tabBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  fontSize: '0.9rem',
  fontWeight: 600,
  color: colors.textSecondary,
  cursor: 'pointer',
};
const tabActive: CSSProperties = { color: colors.action, borderBottom: `2px solid ${colors.action}` };
const countPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 999,
  background: colors.warning,
  color: '#FFFFFF',
  fontSize: '0.7rem',
  fontWeight: 700,
};
const selectStyle: CSSProperties = {
  padding: '5px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '0.82rem',
  background: '#FFFFFF',
  fontFamily: 'inherit',
  color: colors.textPrimary,
};
const caseLink: CSSProperties = { color: colors.action, fontWeight: 600 };
const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', marginBottom: 12 };
